import React, { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../types';
import apiService from '../services/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (
    email: string,
    password: string,
    to?: { company: string; audience: 'staff' | 'portal' }
  ) => Promise<any>;
  logout: () => void;
  register: (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role?: string;
    selectedPlanId?: string;
    startTrial?: boolean;
  }) => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Keep a sign-in (from /auth/login or a hand-off): its tokens and the user,
 * with their role at the company it's for. Returns the user.
 */
export const storeSignIn = (response: any): User => {
  const { token, refreshToken, user: backendUser } = response;

  // Store the tokens. The refresh token renews the login when the access
  // token expires; it wasn't kept, so every login lasted 15 minutes.
  localStorage.setItem('token', token);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

  // Convert backend user to frontend User type
  const frontendUser: User = {
    id: backendUser.id,
    email: backendUser.email,
    firstName: backendUser.firstName,
    lastName: backendUser.lastName,
    role: backendUser.role as 'customer' | 'employee' | 'team_lead' | 'admin',
    isActive: true,
    emailVerified: true,
    preferences: {
      theme: 'light',
      notifications: {
        email: true,
        browser: true,
        ticketAssigned: true,
        ticketUpdated: true,
        ticketResolved: true,
      },
      dashboard: {
        defaultView: 'kanban',
        ticketsPerPage: 25,
      },
    },
    createdAt: new Date(backendUser.createdAt || Date.now()),
    updatedAt: new Date(backendUser.updatedAt || Date.now()),
  };
  localStorage.setItem('user', JSON.stringify(frontendUser));
  // The company it's for: its address is portal.bomizzel.com/<portalName>
  if (response.company) localStorage.setItem('company', JSON.stringify(response.company));
  else localStorage.removeItem('company');
  return frontendUser;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Load user from localStorage on initialization
  React.useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
      } catch (error) {
        console.error('Failed to parse saved user:', error);
        localStorage.removeItem('user');
      }
    }
  }, []);

  const login = async (
    email: string,
    password: string,
    to?: { company: string; audience: 'staff' | 'portal' }
  ) => {
    setIsLoading(true);
    try {
      // Use the backend API for authentication
      const response = await apiService.login(email, password, to);
      setUser(storeSignIn(response));
      return response;
    } catch (error: any) {
      // The API returns `{ error: { code, message } }`. /auth/login used to be
      // the one endpoint that returned `error` as a bare string, and the
      // frontend and backend deploy separately, so accept either shape -
      // otherwise a deploy-order skew shows the user "[object Object]".
      const apiError = error.response?.data?.error;
      const apiMessage = typeof apiError === 'string' ? apiError : apiError?.message;
      // Keep the code so the login page can tell "wrong password" from
      // "email not confirmed yet" (and offer to resend the link).
      const failure = new Error(apiMessage || error.message || 'Login failed') as Error & {
        code?: string;
      };
      failure.code = typeof apiError === 'object' ? apiError?.code : undefined;
      throw failure;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Ask the server to revoke the tokens, but never let signing out depend on
    // that call: the local session is cleared whether or not it reaches us.
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    void apiService.logout(token, refreshToken).catch(() => undefined);

    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('company');
    localStorage.removeItem('refreshToken');
    navigate('/login');
  };

  const register = async (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role?: string;
    selectedPlanId?: string;
    startTrial?: boolean;
  }) => {
    setIsLoading(true);
    try {
      const response = await apiService.register({
        email: userData.email,
        password: userData.password,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role || 'customer',
        selectedPlanId: userData.selectedPlanId,
        startTrial: userData.startTrial,
      });

      // The account can't sign in until its email address is confirmed, so
      // there are no tokens to store; the page tells them to check their inbox.
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    isLoading,
    login,
    logout,
    register,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
